import { LifecycleScriptStatus } from "components/Environment/shared/BuildTypes";
import { getVerbBuildStatusFromTasks } from "components/Environment/utils/environtmentUtils";
import { Environment } from "components/Environment/utils/types";
import { LogViewer } from "components/LogViewer";
import FlatCard from "components/UI-lib/FlatCard";
import { VerbBuildComponent } from "components/Verb/VerbBuildComponent";
import { VerbBuildStatus } from "models/Workspace.model";
import React, { useContext, useEffect } from "react";
import {
  POSTHOG_IDENTITY_FLAGS,
  POSTHOG_PAYLOAD_FLAGS,
  useFeatureFlagPayload,
  useIdentityFeatureFlagEnabled,
} from "utils/posthogFeatureFlags";
import { WorkspaceCreateContext } from "contexts/WorkspaceCreateContext";
import {
  getEnvironmentLogTarget,
  useEnvironmentLogs,
} from "server/devplane/useEnvironmentLogs";
import { PastVMLogs } from "./PastVMLogs";
interface LogsSettingsProps {
  workspaceId: string;
  setShowButton: (b: boolean) => void;
  environment: Environment;
  initialVerbLoad: boolean;
  lifecycleScriptLog: string | null;
}
const LogsHeading: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = "" }) => (
  <>
    <div className={`flex flex-row items-end ${className}`}>
      <div className="mr-3">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-secondary">
          {children}
        </h1>
      </div>
    </div>
    <hr className="w-[100%] my-3 dark:border dark:border-zinc-800" />
  </>
);

const getConfiguredExcludedEnvironmentLogTypes = (
  payload: unknown,
): Set<string> => {
  if (
    !payload ||
    typeof payload !== "object" ||
    !("excludedTypes" in payload)
  ) {
    return new Set();
  }

  const excludedTypes = (payload as { excludedTypes?: unknown }).excludedTypes;
  if (
    !Array.isArray(excludedTypes) ||
    !excludedTypes.every(
      (type) => typeof type === "string" && type.trim().length > 0,
    )
  ) {
    return new Set();
  }

  return new Set(excludedTypes.map((type) => type.trim()));
};

const LogsSettings: React.FC<LogsSettingsProps> = (props) => {
  const { setLogFileContent } = useContext(WorkspaceCreateContext);
  const environmentLogHistoryEnabled =
    useIdentityFeatureFlagEnabled(
      POSTHOG_IDENTITY_FLAGS.INSTANCE_EXEC_LOG_HISTORY,
    ) === true;
  const environmentLogExclusionsPayload = useFeatureFlagPayload<unknown>(
    POSTHOG_PAYLOAD_FLAGS.INSTANCE_EXEC_LOG_EXCLUSIONS,
  );
  const excludedEnvironmentLogTypes = React.useMemo(
    () =>
      getConfiguredExcludedEnvironmentLogTypes(environmentLogExclusionsPayload),
    [environmentLogExclusionsPayload],
  );
  const buildStatus = getVerbBuildStatusFromTasks(
    props.environment.tasks || [],
  );
  const buildIsTerminal =
    buildStatus === VerbBuildStatus.Completed ||
    buildStatus === VerbBuildStatus.CreateFailed;
  const buildLogTarget = getEnvironmentLogTarget(
    props.environment,
    true,
    environmentLogHistoryEnabled,
  );

  useEffect(() => {
    setLogFileContent("");
  }, [
    buildLogTarget.executionKey,
    environmentLogHistoryEnabled,
    setLogFileContent,
  ]);

  useEnvironmentLogs({
    environmentId: buildLogTarget.environmentId,
    executionKey: buildLogTarget.executionKey,
    mode: buildLogTarget.mode,
    onLogs: (content, source) => {
      if (environmentLogHistoryEnabled || source === "persisted") {
        setLogFileContent(content || "\n");
      }
    },
    onError: (error) => {
      console.error(
        "[environment-logs] failed to load environment build logs",
        error,
      );
    },
  });

  const primaryLogLabel = props.environment.build?.vm
    ? "Build logs"
    : "Container Logs";
  const hasScriptLogs = Boolean(props.environment.lifecycle_scripts?.length);
  const primaryLogs = (
    <VerbBuildComponent
      workspaceId={props.workspaceId}
      setShowButton={props.setShowButton}
      environment={props.environment}
      initialVerbLoad={props.initialVerbLoad}
    />
  );
  const scriptLogs = hasScriptLogs ? (
    buildStatus !== VerbBuildStatus.Completed ? (
      <div className="flex w-full">
        <FlatCard isLoading>
          <p className="text-sm text-gray-700 dark:text-secondary font-mono text-center">
            Loading startup script logs...
          </p>
        </FlatCard>
      </div>
    ) : !props.lifecycleScriptLog ? (
      <FlatCard isLoading>
        <p className="text-sm text-gray-700 dark:text-secondary font-mono text-center">
          Loading startup script logs...
        </p>
      </FlatCard>
    ) : (
      <LogViewer
        logs={props.lifecycleScriptLog || ""}
        title={
          props.environment.lifecycle_scripts![0].last_run.status ===
          LifecycleScriptStatus.COMPLETED
            ? "Your script completed successfully"
            : props.environment.lifecycle_scripts![0].last_run.status ===
                LifecycleScriptStatus.FAILED
              ? "Your script ran into an error"
              : "Your script is running..."
        }
        didSucceed={
          props.environment.lifecycle_scripts![0].last_run.status ===
          LifecycleScriptStatus.COMPLETED
        }
        didFail={
          props.environment.lifecycle_scripts![0].last_run.status ===
          LifecycleScriptStatus.FAILED
        }
      />
    )
  ) : null;
  const environmentId =
    props.environment.environment_id || props.environment.workspaceId || "";
  const historicalLogs =
    environmentLogHistoryEnabled && environmentId ? (
      <PastVMLogs
        key={environmentId}
        environmentId={environmentId}
        refreshAfterBuildCompletion={buildIsTerminal}
        excludedEnvironmentLogTypes={excludedEnvironmentLogTypes}
      />
    ) : null;
  return (
    <>
      <LogsHeading>{primaryLogLabel}</LogsHeading>
      <div>{primaryLogs}</div>
      {hasScriptLogs && (
        <>
          <LogsHeading className="mt-8">Startup script logs</LogsHeading>
          {scriptLogs}
        </>
      )}
      {historicalLogs && (
        <>
          <LogsHeading className="mt-8">Other logs</LogsHeading>
          {historicalLogs}
        </>
      )}
    </>
  );
};

export default LogsSettings;
